import { Button, Group, Paper, Stack, Text } from "@mantine/core";
import { useCallback, useMemo, useState } from "react";
import {
  useLeadsSheetStore,
  useLeadsStore,
} from "../../../../../../stores/LeadsStore";
import LeadLeftBio from "./LeadLeftBio";
import { getFieldIcon } from "../../../../community/util";
import { useLanguage } from "../../../../../../stores/LanguageStore";

const defaultFieldsSorting = [
  "%first_name%",
  "%last_name%",
  "%email%",
  "%phone%",
  "%mobile_phone%",
  "%whatsapp%",
  "%linkedin_url%",
  "%contact_location%",
  "%civility%",
  "%job_title%",
];

const companySorting = [
  "%company%",
  "%activity%",
  "%company_size%",
  "%website%",
  "%naf_code%",
  "%city%",
  "%zip_code%",
  "%address%",
  "%gmaps_link%",
  "%siret%",
  "%siren%",
  "%register_ident%",
  "%category%",
  "%company_linkedin_url%",
];

const InfosContact = ({ isCompany }) => {
  const lang = useLanguage((s) => s.language);
  const leadsField = useLeadsStore((s) => s.fields);
  const current = useLeadsSheetStore((s) => s.current);
  const [opened, setOpened] = useState(false);

  const fieldsContact = useMemo(() => {
    if (!current) {
      return { defaultFields: [], customFields: [] };
    }

    const mainFields = new Set([
      ...defaultFieldsSorting,
      ...(isCompany
        ? companySorting
        : leadsField?.map((x) => x.identifier_placeholder) || []),
    ]);

    const result = [...mainFields].map((identifier_placeholder) => {
      let element = leadsField?.find(
        (x) => x.identifier_placeholder === identifier_placeholder
      );

      if (element) {
        let value =
          element?.value ||
          current.properties.find((x) => x.id === element?.id)?.value;

        return {
          field: element.identifier_placeholder,
          field_id: element.id,
          name: element.name,
          defaultValue: value,
          readonly: element?.readonly,
        };
      } else {
        // Whatsapp
        if (identifier_placeholder === "%whatsapp%") {
          let phone = current.properties.find(
            (x) => x.identifier_placeholder === "%phone%"
          )?.value;
          let mobile_phone = current.properties.find(
            (x) => x.identifier_placeholder === "%mobile_phone%"
          )?.value;

          return {
            field: "%whatsapp%",
            field_id: null,
            name: "WhatsApp",
            defaultValue: `https://wa.me/${mobile_phone || phone || ""}`,
            readonly: true,
          };
        }

        // GMaps
        if (identifier_placeholder === "%gmaps_link%") {
          let company = current.properties.find(
            (x) => x.identifier_placeholder === "%company%"
          )?.value;
          let website = current.properties.find(
            (x) => x.identifier_placeholder === "%website%"
          )?.value;
          let link =
            "https://www.google.com/maps/search/" +
            (company?.replaceAll(" ", "+") || "") +
            (website ? `+${website}` : "");

          return {
            field: "%gmaps_link%",
            field_id: null,
            name: "GMaps",
            defaultValue: link,
            readonly: true,
          };
        }
      }
    });

    return result;
  }, [current, isCompany, leadsField]);  

  return (
    <>
      <Text size="sm" fw={600}>
        {isCompany ? lang.leads.company_info : lang.leads.contact_info}
      </Text>

      <Paper
        component={Stack}
        w={"100%"}
        gap={0}
        p={"sm"}
        py={"xs"}
        mt={-8}
        pos={"relative"}
      >
        <Group
          h={opened ? "auto" : 196}
          style={{ overflow: opened ? "auto" : "hidden" }}
          gap={0}
          align="start"
        >
          {fieldsContact
            .filter((x) => {
              if (isCompany) {
                return companySorting.includes(x?.field);
              }
              return !companySorting.includes(x?.field);
            })
            .map((item, index) => (
              <LeadLeftBio
                key={item?.field_id}
                defaultValue={item?.defaultValue}
                label={item?.name}
                field={item?.field}
                fieldId={item?.field_id}
                icon={item?.field && getFieldIcon(item.field)}
                isLink={item?.defaultValue?.includes("http") || item?.field === "%website%"}
                isTel={item?.field.includes("phone")}
                isTelMobile={item?.field.includes("mobile_phone")}
                isMail={
                  item?.field.includes("mail") && item?.field.includes("@")
                }
                phoneInCharge={current?.phone_has_person_in_charge}
                disableEdit={
                  !current?.permission?.write_permission || item?.readonly
                }
              />
            ))}
        </Group>

        <Button
          variant="subtle"
          size="compact-sm"
          onClick={() => setOpened(!opened)}
          ml={"auto"}
          mt={0}
          style={
            opened
              ? {
                  position: "absolute",
                  top: 2,
                  right: 4,
                }
              : {}
          }
        >
          {opened ? " - " : " + "} {lang.leads.infos}
        </Button>
      </Paper>
    </>
  );
};

export default InfosContact;
